/**
 * This file contains many functions that make various calls to our
 * delivery API endpoint and return the promises from those calls.
 */
import queryString from 'querystring';
import { omit, assign, flatMap, range } from 'lodash';
import Api from '../api.ts';

const BOOKINGS = '/bookings';
const DELIVERIES = `${BOOKINGS}/deliveries`;
const BLOCKS = `${BOOKINGS}/blocks`;

const DeliveryAPI = {
  getDeliveryAvailabilities(params = {}) {
    return Api.get(
      `${BOOKINGS}/delivery-availabilities?${queryString.encode(params)}`,
    );
  },

  findBookings(reference) {
    return Api.get(`${BOOKINGS}/search/reference/${reference}`);
  },

  createDelivery(delivery, purchaseOrderNumbers) {
    const data = { delivery, purchaseOrderNumbers };
    return Api.post(`${DELIVERIES}`, { data });
  },

  getOneDelivery(deliveryId) {
    return Api.get(`${DELIVERIES}/${deliveryId}`);
  },

  async getBookingsExport(options) {
    const limit = 100;
    const firstPage = await Api.get(
      `${BOOKINGS}?${queryString.encode(
        assign(
          {
            page: 1,
          },
          options,
        ),
      )}`,
    );

    const { count } = firstPage;
    const totalPages = Math.ceil(count / limit);

    const deliveries = await Promise.all(
      range(totalPages).map(async (page) => {
        const params = queryString.encode(
          assign({ page: page + 1, limit }, options),
        );
        return Api.get(`${BOOKINGS}?${params}`);
      }),
    );

    return flatMap(deliveries, 'bookings');
  },

  get(options = {}) {
    return Api.get(`${BOOKINGS}?${queryString.encode(options)}`);
  },

  updateDelivery(deliveryId, delivery, purchaseOrderNumbers) {
    const data = {
      delivery: omit(delivery, 'purchase_orders'),
      purchaseOrderNumbers,
    };
    return Api.put(`${DELIVERIES}/${deliveryId}`, { data });
  },

  removeDelivery(deliveryId, reason) {
    const params = `cancellation_reason=${reason}`;
    return Api.delete(`${DELIVERIES}/${deliveryId}?${params}`);
  },

  createBlock(block) {
    const data = { block };
    return Api.post(`${BLOCKS}`, { data });
  },

  getBlock(blockId) {
    return Api.get(`${BOOKINGS}/${blockId}`);
  },

  updateBlock(block, blockId) {
    const data = { block };
    return Api.put(`${BLOCKS}/${blockId}`, { data });
  },

  removeBlock(blockId) {
    return Api.delete(`${BLOCKS}/${blockId}`);
  },
};

export default DeliveryAPI;
